import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Bot,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronRight,
  CircleDollarSign,
  CloudOff,
  Compass,
  Cpu,
  ExternalLink,
  FileCheck2,
  Gauge,
  Headphones,
  Home,
  Laptop,
  Leaf,
  LocateFixed,
  Map,
  MapPin,
  Menu,
  MessageCircle,
  Mic,
  MicOff,
  MonitorSmartphone,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Route as RouteIcon,
  Save,
  Send,
  Settings2,
  ShieldCheck,
  Smartphone,
  SquareArrowOutUpRight,
  Store,
  X,
} from "lucide-react";
import { Badge, Button, Card, EmptyState, Metric, Timestamp } from "./components/ui";
import {
  buildTime,
  ensureDeviceProfile,
  getBootstrap,
  getCaptures,
  getHealth,
  getSetting,
  probeMcp,
  registerDevice,
  saveCapture,
  sendChat,
  setSetting,
  shellConfig,
  type BootstrapPayload,
  type CaptureItem,
  type ChatMessage,
  type DeviceProfile,
  type HealthPayload,
} from "./lib/shell";

const navItems = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/map", label: "Map", icon: Map },
  { to: "/capture", label: "Capture", icon: Mic },
  { to: "/chat", label: "Conductor", icon: MessageCircle },
  { to: "/devices", label: "Devices", icon: MonitorSmartphone },
];

function relativeTime(value?: string | null): string {
  if (!value) return "awaiting signal";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.round(diff / 60_000));
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function shortId(value?: string): string {
  return value ? `${value.slice(0, 8)}…${value.slice(-4)}` : "pending";
}

function AppFrame({
  children,
  online,
  installPrompt,
  onInstall,
  updateReady,
  onUpdate,
}: {
  children: ReactNode;
  online: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  onInstall: () => void;
  updateReady: boolean;
  onUpdate: () => void;
}) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 820);
  const current = navItems.find((item) => item.to === location.pathname)?.label || "Home";

  return (
    <div className={`shell-layout ${sidebarOpen ? "shell-layout--open" : "shell-layout--compact"}`}>
      <aside className="sidebar" aria-label="Primary navigation">
        <div className="brand-lockup">
          <img src="/shell-mark.svg" alt="" width="48" height="48" />
          <div className="brand-lockup__copy">
            <span>{shellConfig.name}</span>
            <small>{shellConfig.target === "rg" ? "Field shell" : "Life shell"}</small>
          </div>
          <button className="icon-button sidebar-toggle" onClick={() => setSidebarOpen((value) => !value)} aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}>
            {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </button>
        </div>

        <nav className="nav-stack">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => { if (window.innerWidth <= 820) setSidebarOpen(false); }} className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}>
              <Icon size={19} strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <a className="peer-shell" href={shellConfig.peerUrl}>
            <span><Leaf size={16} /> Open {shellConfig.peerName}</span>
            <ExternalLink size={14} />
          </a>
          <div className="mini-status"><span className={online ? "pulse pulse--good" : "pulse pulse--bad"} />{online ? "Network available" : "Offline workspace"}</div>
        </div>
      </aside>

      <header className="topbar">
        <button className="icon-button mobile-menu" onClick={() => setSidebarOpen((value) => !value)} aria-label="Toggle navigation"><Menu size={20} /></button>
        <div>
          <span className="topbar__eyebrow">{shellConfig.eyebrow}</span>
          <h1>{current}</h1>
        </div>
        <div className="topbar__actions">
          <Badge tone={online ? "good" : "warn"}>{online ? "Live" : "Offline"}</Badge>
          {installPrompt && <Button variant="secondary" onClick={onInstall}><SquareArrowOutUpRight size={16} /> Install</Button>}
        </div>
      </header>

      {updateReady && <div className="update-banner"><Leaf size={16} /><span>A quieter, sharper shell is ready.</span><Button variant="ghost" onClick={onUpdate}>Reload now</Button></div>}

      <main className="main-content">{children}</main>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => isActive ? "bottom-nav__link bottom-nav__link--active" : "bottom-nav__link"}>
            <Icon size={20} /><span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function SectionHeader({ eyebrow, title, copy, action }: { eyebrow: string; title: string; copy: string; action?: ReactNode }) {
  return <div className="section-header"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2><p>{copy}</p></div>{action}</div>;
}

function Dashboard({ health, bootstrap, profile, registration, refreshing, onRefresh }: {
  health: HealthPayload | null;
  bootstrap: BootstrapPayload | null;
  profile: DeviceProfile | null;
  registration: "pending" | "registered" | "local";
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const isRg = shellConfig.target === "rg";
  const coreOk = health?.dependencies.core.ok === true;
  const businessOk = health?.dependencies.business.ok === true;

  return (
    <div className="surface-stack">
      <section className="hero-panel">
        <div className="hero-panel__copy">
          <span className="eyebrow">{isRg ? "Tuesday field board" : "Tuesday · first light"}</span>
          <h2>{isRg ? "The work that matters, before the noise." : "Good morning, Evan."}</h2>
          <p>{isRg ? "Client context, field capture, route awareness, and safe execution drafts—one calm surface." : "Your day is assembled from live signals. Review what changed, capture what matters, then move."}</p>
          <div className="hero-panel__actions">
            <NavLink className="button button--primary" to="/capture"><Mic size={17} /> Capture now</NavLink>
            <NavLink className="button button--secondary" to="/chat"><MessageCircle size={17} /> Ask the conductor</NavLink>
          </div>
        </div>
        <img className="hero-panel__branch" src="/olive-branch.svg" alt="" />
        <div className="hero-panel__signal">
          <span>System pulse</span>
          <strong>{coreOk && businessOk ? "Clear" : "Check signals"}</strong>
          <Timestamp value={health?.checked_at} label="Measured" />
        </div>
      </section>

      <div className="metrics-grid">
        <Metric label={isRg ? "Recent client days" : "Open actions"} value={isRg ? bootstrap?.metrics.recent_client_sessions ?? "—" : bootstrap?.metrics.open_actions ?? "—"} note={isRg ? "last seven days" : "review queue"} />
        <Metric label="Registered shell installs" value={bootstrap?.metrics.registered_devices ?? health?.registered_devices ?? "—"} note={`${shellConfig.name} scope`} />
        <Metric label="Daily brief" value={bootstrap?.brief?.date || "Waiting"} note={bootstrap?.brief ? relativeTime(bootstrap.brief.generated_at) : "no snapshot"} />
        <Metric label="This device" value={registration === "registered" ? "Enrolled" : "Local"} note={profile?.displayMode === "standalone" ? "installed PWA" : "browser session"} />
      </div>

      <div className="dashboard-grid">
        <Card className="brief-card">
          <div className="card__header"><div><span className="eyebrow">Daily brief</span><h3>{isRg ? "R&G operating picture" : "SAGE situation room"}</h3></div><Button variant="ghost" onClick={onRefresh} disabled={refreshing} aria-label="Refresh live signals"><RefreshCw size={17} className={refreshing ? "spin" : ""} /></Button></div>
          <div className="brief-list">
            {(isRg ? [
              ["Route", "Map mode is ready for today's field sequence.", RouteIcon],
              ["Client context", `${bootstrap?.metrics.recent_client_sessions ?? "—"} fused client-days recorded this week.`, BriefcaseBusiness],
              ["Approval rail", "Every action remains a draft until Evan approves it.", ShieldCheck],
            ] : [
              ["Morning context", bootstrap?.brief ? `Brief generated ${relativeTime(bootstrap.brief.generated_at)}.` : "Waiting for today's brief snapshot.", CalendarDays],
              ["Ecosystem", coreOk ? "Core intelligence is answering cleanly." : "Core intelligence needs attention.", Activity],
              ["Capture rail", "Voice and typed notes stay local until review.", FileCheck2],
            ]).map(([title, copy, Icon]) => {
              const RowIcon = Icon as typeof Activity;
              return <div className="brief-row" key={String(title)}><span className="brief-row__icon"><RowIcon size={18} /></span><div><strong>{String(title)}</strong><p>{String(copy)}</p></div><ChevronRight size={16} /></div>;
            })}
          </div>
          <Timestamp value={bootstrap?.generated_at} label="Data checked" />
        </Card>

        <Card className="status-card">
          <div className="card__header"><div><span className="eyebrow">Live services</span><h3>Signal integrity</h3></div><Network size={20} /></div>
          <div className="service-list">
            <div><span className={coreOk ? "status-orb status-orb--good" : "status-orb status-orb--bad"} /><p><strong>SAGE Core</strong><small>{coreOk ? "D1 and MCP worker healthy" : "No clean response"}</small></p><span>{relativeTime(health?.dependencies.core.ts as string | undefined)}</span></div>
            <div><span className={businessOk ? "status-orb status-orb--good" : "status-orb status-orb--bad"} /><p><strong>SAGE Business</strong><small>{businessOk ? "Business tools available" : "No clean response"}</small></p><span>{relativeTime(health?.dependencies.business.ts as string | undefined)}</span></div>
            <div><span className={registration === "registered" ? "status-orb status-orb--good" : "status-orb status-orb--warn"} /><p><strong>Device registry</strong><small>{registration === "registered" ? shortId(profile?.deviceId) : "Working locally"}</small></p><span>{profile?.displayMode}</span></div>
          </div>
          <Timestamp value={health?.checked_at} />
        </Card>

        <Card className="map-preview-card">
          <div className="mini-map" aria-label="Stylized Cape Cod route preview">
            <div className="mini-map__land" />
            <span className="map-pin map-pin--one"><span>1</span></span>
            <span className="map-pin map-pin--two"><span>2</span></span>
            <span className="map-pin map-pin--three"><span>3</span></span>
            <div className="mini-map__route" />
          </div>
          <div className="map-preview-card__copy"><span className="eyebrow">First-class map</span><h3>{isRg ? "Field route" : "Life in place"}</h3><p>{isRg ? "See the day spatially before deciding what comes next." : "Places, movement, and context belong beside the brief—not buried in a menu."}</p><NavLink to="/map">Open map mode <ArrowRight size={15} /></NavLink></div>
        </Card>

        <Card className="tool-card">
          <div className="card__header"><div><span className="eyebrow">{isRg ? "Field tools" : "Quick paths"}</span><h3>{isRg ? "Safe execution drafts" : "Move with less friction"}</h3></div><Gauge size={20} /></div>
          <div className="tool-grid">
            {(isRg ? [
              ["Client platform", "Open", "https://app.ccrestaurantconsulting.com", Store],
              ["Network probe", "Read only", "/capture", Network],
              ["Quote context", "Review", "https://app.ccrestaurantconsulting.com", CircleDollarSign],
              ["Visit capture", "Start", "/capture", MapPin],
            ] : [
              ["Daily capture", "Open", "/capture", Mic],
              ["Device mesh", "Review", "/devices", Cpu],
              ["Map mode", "Open", "/map", Compass],
              ["Conductor", "Ask", "/chat", Bot],
            ]).map(([label, meta, href, Icon]) => {
              const ToolIcon = Icon as typeof Activity;
              const external = String(href).startsWith("http");
              const content = <><span><ToolIcon size={18} /></span><p><strong>{String(label)}</strong><small>{String(meta)}</small></p>{external ? <ExternalLink size={14} /> : <ChevronRight size={15} />}</>;
              return external ? <a className="tool-link" href={String(href)} key={String(label)}>{content}</a> : <NavLink className="tool-link" to={String(href)} key={String(label)}>{content}</NavLink>;
            })}
          </div>
          <Timestamp value={bootstrap?.generated_at} label="Context checked" />
        </Card>
      </div>
    </div>
  );
}

function MapSurface() {
  const [position, setPosition] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const isRg = shellConfig.target === "rg";

  const locate = () => {
    setLocating(true);
    setError("");
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => { setPosition({ lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy }); setLocating(false); },
      (reason) => { setError(reason.message); setLocating(false); },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  };

  const stops = isRg ? [
    { time: "08:30", name: "Morning field window", note: "Review live client context before departure", status: "ready" },
    { time: "11:00", name: "On-site diagnostic", note: "Capture first; draft execution second", status: "next" },
    { time: "15:30", name: "Follow-up lane", note: "Confirm promises and unbilled context", status: "later" },
  ] : [
    { time: "Now", name: "Current place", note: "Location is only stored when you choose to capture it", status: "ready" },
    { time: "Next", name: "Context nearby", note: "Calendar, movement, and open threads converge here", status: "next" },
    { time: "Later", name: "Return home", note: "Close the loop with a reviewed daily synthesis", status: "later" },
  ];

  return <div className="surface-stack">
    <SectionHeader eyebrow="Map mode" title={isRg ? "The field day, spatially." : "Context has a place."} copy={isRg ? "A first-class route view for visits, captured evidence, and what needs your attention next." : "Your map remains useful without tracking you by default. Location is requested only when you tap locate."} action={<Button onClick={locate} disabled={locating}><LocateFixed size={17} />{locating ? "Locating…" : "Locate me"}</Button>} />
    <div className="map-layout">
      <Card className="map-canvas-card">
        <div className="map-canvas">
          <div className="map-canvas__water" />
          <div className="map-canvas__cape" />
          <div className="map-canvas__roads map-canvas__roads--one" />
          <div className="map-canvas__roads map-canvas__roads--two" />
          <span className="map-label map-label--one">Upper Cape</span><span className="map-label map-label--two">Mid Cape</span><span className="map-label map-label--three">Outer Cape</span>
          {stops.map((stop, index) => <button key={stop.name} className={`map-marker map-marker--${index + 1}`} aria-label={stop.name}><span>{index + 1}</span></button>)}
          {position && <div className="current-position"><span /><small>You · ±{Math.round(position.accuracy)}m</small></div>}
          <div className="map-scale">10 mi</div>
        </div>
        <div className="map-meta"><Badge tone={position ? "good" : "neutral"}>{position ? "Location available" : "Location private"}</Badge><Timestamp value={position ? new Date().toISOString() : null} label="Position" /></div>
      </Card>
      <div className="route-panel">
        <div className="route-panel__header"><div><span className="eyebrow">{isRg ? "Today's sequence" : "Place rhythm"}</span><h3>{isRg ? "Three deliberate stops" : "A light spatial plan"}</h3></div><RouteIcon size={22} /></div>
        {stops.map((stop, index) => <div className="route-stop" key={stop.name}><div className="route-stop__line"><span>{index + 1}</span></div><div><small>{stop.time}</small><strong>{stop.name}</strong><p>{stop.note}</p><Badge tone={stop.status === "ready" ? "good" : stop.status === "next" ? "sage" : "neutral"}>{stop.status}</Badge></div></div>)}
        {error && <p className="inline-error">{error}</p>}
        <Timestamp value={new Date().toISOString()} label="Route viewed" />
      </div>
    </div>
  </div>;
}

function CaptureSurface() {
  const [text, setText] = useState("");
  const [captures, setCaptures] = useState<CaptureItem[]>([]);
  const [listening, setListening] = useState(false);
  const [notice, setNotice] = useState("");
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const hasSpeech = Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const load = useCallback(() => getCaptures().then(setCaptures).catch(() => setCaptures([])), []);
  useEffect(() => { void load(); return () => recognitionRef.current?.stop(); }, [load]);

  const toggleVoice = () => {
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { setNotice("Voice capture is unavailable in this browser. Typed capture remains ready."); return; }
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map((result) => result[0]?.transcript || "").join(" ");
      setText(transcript.trim());
    };
    recognition.onerror = (event) => { setNotice(`Voice capture paused: ${event.error}.`); setListening(false); };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
    setNotice("Listening. Nothing leaves this device until you review it.");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    const item: CaptureItem = { id: crypto.randomUUID(), text: value, scope: shellConfig.target, createdAt: new Date().toISOString(), status: "pending_review", requires_evan_approval: 1, source: listening ? "voice" : "typed" };
    recognitionRef.current?.stop();
    await saveCapture(item);
    setText("");
    setListening(false);
    setNotice("Captured locally and placed in review. Nothing was published.");
    await load();
  };

  const updateStatus = async (item: CaptureItem, status: CaptureItem["status"]) => { await saveCapture({ ...item, status }); await load(); };

  return <div className="surface-stack">
    <SectionHeader eyebrow="Voice first · approval always" title={shellConfig.target === "rg" ? "Capture the field before it disappears." : "Catch the thought. Keep your footing."} copy="Speak or type in one place. Every item stays on this device in a review queue with requires_evan_approval = 1." />
    <div className="capture-layout">
      <Card className={`capture-composer ${listening ? "capture-composer--listening" : ""}`}>
        <div className="capture-composer__top"><div className="listening-orb"><span /><Mic size={24} /></div><div><span className="eyebrow">{listening ? "Listening now" : "Private capture"}</span><h3>{listening ? "Say what matters." : "Ready when you are."}</h3></div></div>
        <form onSubmit={submit}>
          <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={shellConfig.target === "rg" ? "What did you observe, promise, measure, or need to follow up on?" : "What are you noticing, deciding, remembering, or trying not to lose?"} rows={8} maxLength={8_000} />
          <div className="capture-composer__footer"><span>{text.length.toLocaleString()} / 8,000</span><div><Button type="button" variant={listening ? "danger" : "secondary"} onClick={toggleVoice}>{listening ? <MicOff size={17} /> : <Mic size={17} />}{listening ? "Stop" : hasSpeech ? "Speak" : "Voice unavailable"}</Button><Button type="submit" disabled={!text.trim()}><Save size={17} /> Save to review</Button></div></div>
        </form>
        {notice && <div className="capture-notice"><ShieldCheck size={16} />{notice}</div>}
        <Timestamp value={new Date().toISOString()} label="Local clock" />
      </Card>

      <Card className="review-queue">
        <div className="card__header"><div><span className="eyebrow">Tap-first review</span><h3>Capture queue</h3></div><Badge tone={captures.some((item) => item.status === "pending_review") ? "warn" : "good"}>{captures.filter((item) => item.status === "pending_review").length} pending</Badge></div>
        {captures.length === 0 ? <EmptyState icon={<FileCheck2 size={26} />} title="The review rail is clear.">Your next capture will appear here. It will never auto-publish.</EmptyState> : <div className="capture-list">{captures.slice(0, 10).map((item) => <article className={`capture-item capture-item--${item.status}`} key={item.id}><div className="capture-item__meta"><Badge tone={item.status === "pending_review" ? "warn" : item.status === "approved_for_draft" ? "good" : "neutral"}>{item.status.replaceAll("_", " ")}</Badge><Timestamp value={item.createdAt} label={item.source} /></div><p>{item.text}</p><div className="capture-item__actions">{item.status === "pending_review" && <Button variant="secondary" onClick={() => updateStatus(item, "approved_for_draft")}><Check size={15} /> Approve for draft</Button>}{item.status !== "archived" && <Button variant="ghost" onClick={() => updateStatus(item, "archived")}>Archive</Button>}</div></article>)}</div>}
        <Timestamp value={captures[0]?.createdAt} label="Last capture" />
      </Card>
    </div>
  </div>;
}

function ChatSurface() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [coreToken, setCoreToken] = useState("");
  const [businessToken, setBusinessToken] = useState("");
  const [model, setModel] = useState("claude-opus-4-20250514");
  const [notice, setNotice] = useState("");
  const [probing, setProbing] = useState(false);

  useEffect(() => { void Promise.all([getSetting("anthropic_api_key"), getSetting("core_mcp_token"), getSetting("business_mcp_token"), getSetting("anthropic_model")]).then(([key, core, business, savedModel]) => { setApiKey(key); setCoreToken(core); setBusinessToken(business); if (savedModel) setModel(savedModel); }); }, []);

  const saveSettings = async () => {
    await Promise.all([setSetting("anthropic_api_key", apiKey.trim()), setSetting("core_mcp_token", coreToken.trim()), setSetting("business_mcp_token", businessToken.trim()), setSetting("anthropic_model", model.trim())]);
    setNotice("Saved to this device's IndexedDB. No credential was added to the app bundle.");
  };

  const testMcp = async () => {
    const token = shellConfig.target === "rg" ? businessToken || coreToken : coreToken;
    const scope = shellConfig.target === "rg" && businessToken ? "business" : "core";
    if (!token) { setNotice("Add an MCP bearer first."); return; }
    setProbing(true);
    try { const result = await probeMcp(scope, token); setNotice(result.sessionId ? `MCP round-trip passed. Session ${shortId(result.sessionId)}.` : "MCP answered without a session header."); }
    catch (error) { setNotice(`MCP probe failed: ${error instanceof Error ? error.message : String(error)}`); }
    finally { setProbing(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    if (!apiKey) { setSettingsOpen(true); setNotice("Add your Anthropic API key to this device before sending."); return; }
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", content, createdAt: new Date().toISOString() };
    const next = [...messages, userMessage];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const response = await sendChat({ apiKey, model, target: shellConfig.target, messages: next, coreToken, businessToken });
      setMessages((current) => [...current, { id: crypto.randomUUID(), role: "assistant", content: response, createdAt: new Date().toISOString() }]);
      setNotice("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally { setSending(false); }
  };

  return <div className="surface-stack chat-surface">
    <SectionHeader eyebrow="MCP-aware conductor" title={shellConfig.target === "rg" ? "Ask with the business in view." : "Ask with your ecosystem in view."} copy="Keys stay in this device's IndexedDB and are sent only for the request. MCP access is restricted to a read-only allowlist." action={<Button variant="secondary" onClick={() => setSettingsOpen((value) => !value)}><Settings2 size={17} />{settingsOpen ? "Close setup" : "Connection setup"}</Button>} />
    {settingsOpen && <Card className="settings-panel">
      <div className="settings-panel__header"><div><span className="eyebrow">Device-local credentials</span><h3>Connection setup</h3><p>The production bundle contains no keys. Values below remain in this browser profile.</p></div><ShieldCheck size={24} /></div>
      <div className="settings-grid">
        <label><span>Anthropic API key</span><input type="password" autoComplete="off" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="Required to send messages" /></label>
        <label><span>Model ID</span><select value={model} onChange={(event) => setModel(event.target.value)}><option value="claude-opus-4-20250514">Claude Opus 4</option><option value="claude-sonnet-4-20250514">Claude Sonnet 4</option><option value="claude-3-5-haiku-20241022">Claude Haiku 3.5</option></select></label>
        <label><span>SAGE Core MCP bearer</span><input type="password" autoComplete="off" value={coreToken} onChange={(event) => setCoreToken(event.target.value)} placeholder="Optional read-only context" /></label>
        {shellConfig.target === "rg" && <label><span>SAGE Business MCP bearer</span><input type="password" autoComplete="off" value={businessToken} onChange={(event) => setBusinessToken(event.target.value)} placeholder="Optional R&G context" /></label>}
      </div>
      <div className="settings-panel__actions"><Button onClick={saveSettings}><Save size={16} /> Save on this device</Button><Button variant="secondary" onClick={testMcp} disabled={probing}><Network size={16} />{probing ? "Probing…" : "Test MCP round-trip"}</Button></div>
      {notice && <div className="inline-notice">{notice}</div>}
      <Timestamp value={new Date().toISOString()} label="Setup viewed" />
    </Card>}

    <Card className="chat-panel">
      <div className="chat-panel__status"><div><span className="conductor-avatar"><Bot size={20} /></span><p><strong>{shellConfig.target === "rg" ? "R&G Conductor" : "SAGE Conductor"}</strong><small>{model.replaceAll("-", " ")}</small></p></div><div><Badge tone={coreToken || businessToken ? "good" : "neutral"}>{coreToken || businessToken ? "MCP configured" : "MCP optional"}</Badge><Badge tone="sage">Read only</Badge></div></div>
      <div className="message-list" aria-live="polite">
        {messages.length === 0 ? <div className="chat-welcome"><img src="/olive-branch.svg" alt="" /><h3>One conversation, grounded in your own systems.</h3><p>{shellConfig.target === "rg" ? "Ask for today's client picture, unbilled context, route considerations, or a safe action draft." : "Ask for a daily synthesis, system signal, recent context, or help shaping the next move."}</p><div className="prompt-chips">{(shellConfig.target === "rg" ? ["What needs my attention first?", "Summarize the field day", "Draft a safe follow-up plan"] : ["What changed since yesterday?", "Give me the quiet version of today", "Which signal deserves attention?"]).map((prompt) => <button onClick={() => setInput(prompt)} key={prompt}>{prompt}</button>)}</div></div> : messages.map((message) => <article key={message.id} className={`message message--${message.role}`}><span>{message.role === "assistant" ? <Bot size={17} /> : "ER"}</span><div><p>{message.content}</p><Timestamp value={message.createdAt} label={message.role} /></div></article>)}
        {sending && <article className="message message--assistant"><span><Bot size={17} /></span><div className="thinking"><i /><i /><i /></div></article>}
      </div>
      <form className="chat-composer" onSubmit={submit}><textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder="Ask, plan, or synthesize…" rows={2} maxLength={12_000} /><Button type="submit" disabled={!input.trim() || sending} aria-label="Send message"><Send size={18} /></Button></form>
      {!settingsOpen && notice && <div className="inline-error">{notice}</div>}
      <div className="chat-panel__footer"><span><ShieldCheck size={14} /> No publish or write tools</span><Timestamp value={messages.at(-1)?.createdAt || buildTime} label="Conversation" /></div>
    </Card>
  </div>;
}

function DevicesSurface({ profile, registration, onRegister }: { profile: DeviceProfile | null; registration: "pending" | "registered" | "local"; onRegister: () => void }) {
  const capabilities = profile ? Object.entries(profile.capabilities) : [];
  return <div className="surface-stack">
    <SectionHeader eyebrow="Force-enrollment rail" title="Every capable device becomes visible." copy="This install has a durable device ID in localStorage and IndexedDB. Registration records capabilities—not personal content." action={<Button onClick={onRegister} disabled={!profile || registration === "pending"}><RefreshCw size={17} className={registration === "pending" ? "spin" : ""} />{registration === "pending" ? "Registering…" : "Refresh enrollment"}</Button>} />
    <div className="device-grid">
      <Card className="device-identity-card">
        <div className="device-illustration"><Laptop size={56} strokeWidth={1.3} /><span className={registration === "registered" ? "status-orb status-orb--good" : "status-orb status-orb--warn"} /></div>
        <span className="eyebrow">This installation</span><h3>{profile?.label || "Building device profile…"}</h3><p>{profile?.displayMode === "standalone" ? "Installed as a standalone PWA." : "Running in a browser tab. Install for a focused app window and stronger offline behavior."}</p>
        <dl><div><dt>Device ID</dt><dd>{shortId(profile?.deviceId)}</dd></div><div><dt>Installation</dt><dd>{shortId(profile?.installationId)}</dd></div><div><dt>Scope</dt><dd>{shellConfig.longName}</dd></div><div><dt>Status</dt><dd><Badge tone={registration === "registered" ? "good" : "warn"}>{registration}</Badge></dd></div></dl>
        <Timestamp value={new Date().toISOString()} label="Profile read" />
      </Card>

      <Card className="capability-card">
        <div className="card__header"><div><span className="eyebrow">Capability profile</span><h3>What this device can offer</h3></div><Cpu size={22} /></div>
        <div className="capability-grid">{capabilities.map(([key, enabled]) => <div className={enabled ? "capability capability--enabled" : "capability"} key={key}><span>{enabled ? <Check size={15} /> : <X size={15} />}</span><p>{key.replaceAll("_", " ")}</p></div>)}</div>
        <p className="privacy-note"><ShieldCheck size={15} /> Permissions are never requested until a surface actually needs them.</p>
        <Timestamp value={new Date().toISOString()} label="Capabilities checked" />
      </Card>

      <Card className="mesh-card">
        <div className="mesh-visual"><span className="mesh-node mesh-node--center"><img src="/shell-mark.svg" alt="" /></span><span className="mesh-node mesh-node--one"><Smartphone size={22} /></span><span className="mesh-node mesh-node--two"><Laptop size={22} /></span><span className="mesh-node mesh-node--three"><Headphones size={22} /></span><i className="mesh-line mesh-line--one" /><i className="mesh-line mesh-line--two" /><i className="mesh-line mesh-line--three" /></div>
        <div><span className="eyebrow">Device mesh</span><h3>Thin clients, one source of truth.</h3><p>Installations identify their scope and capabilities. Durable state stays in D1; local secrets stay local.</p></div>
        <Timestamp value={new Date().toISOString()} label="Mesh viewed" />
      </Card>
    </div>
  </div>;
}

export function App() {
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [profile, setProfile] = useState<DeviceProfile | null>(null);
  const [registration, setRegistration] = useState<"pending" | "registered" | "local">("pending");
  const [online, setOnline] = useState(navigator.onLine);
  const [refreshing, setRefreshing] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [updateReady, setUpdateReady] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [nextHealth, nextBootstrap] = await Promise.all([getHealth(), getBootstrap(shellConfig.target)]);
      setHealth(nextHealth);
      setBootstrap(nextBootstrap);
    } catch {
      setOnline(navigator.onLine && false);
    } finally { setRefreshing(false); }
  }, []);

  const enroll = useCallback(async (knownProfile?: DeviceProfile) => {
    const nextProfile = knownProfile || profile || await ensureDeviceProfile(shellConfig.target);
    setProfile(nextProfile);
    setRegistration("pending");
    try { await registerDevice(nextProfile); setRegistration("registered"); }
    catch { setRegistration("local"); }
  }, [profile]);

  useEffect(() => {
    void ensureDeviceProfile(shellConfig.target).then((nextProfile) => { setProfile(nextProfile); void enroll(nextProfile); });
    void refresh();
    const onOnline = () => { setOnline(true); void refresh(); };
    const onOffline = () => setOnline(false);
    const onInstallPrompt = (event: BeforeInstallPromptEvent) => { event.preventDefault(); setInstallPrompt(event); };
    const onUpdate = () => setUpdateReady(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    window.addEventListener("sage:sw-update", onUpdate);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); window.removeEventListener("beforeinstallprompt", onInstallPrompt); window.removeEventListener("sage:sw-update", onUpdate); };
  }, [enroll, refresh]);

  const install = () => { void installPrompt?.prompt(); setInstallPrompt(null); };
  const update = () => { navigator.serviceWorker.controller?.postMessage({ type: "SKIP_WAITING" }); window.location.reload(); };
  const dashboard = useMemo(() => <Dashboard health={health} bootstrap={bootstrap} profile={profile} registration={registration} refreshing={refreshing} onRefresh={refresh} />, [health, bootstrap, profile, registration, refreshing, refresh]);

  return <AppFrame online={online} installPrompt={installPrompt} onInstall={install} updateReady={updateReady} onUpdate={update}>
    <Routes>
      <Route path="/" element={dashboard} />
      <Route path="/sage/dashboard" element={dashboard} />
      <Route path="/rg/dashboard" element={dashboard} />
      <Route path="/map" element={<MapSurface />} />
      <Route path="/capture" element={<CaptureSurface />} />
      <Route path="/chat" element={<ChatSurface />} />
      <Route path="/devices" element={<DevicesSurface profile={profile} registration={registration} onRegister={() => void enroll()} />} />
      <Route path="*" element={dashboard} />
    </Routes>
  </AppFrame>;
}
